/** @jsx jsx */
import { jsx } from '@emotion/react';
import { MouseEvent as ReactMouseEvent } from 'react';
import { Colors } from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';

import { SIDEBAR_ID } from '../constants';
import {
  UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
  UNIVERSAL_SIDEBAR_WIDTH,
} from '../../../constants';
import { CreateDropdown } from './CreateDropdown';
import { Divider } from '../Divider';
import Footer from './Footer';
import InAppLinks from './InAppLinks';
import InAppSidebarSubheaderButton from './InAppSidebarSubheaderButton';
import { useQuerySidebar } from './useQuerySidebar';
import {
  useUniversalNavContext,
  useQuerySession,
  useQueryTaskUsageTreatment,
} from '../../../hooks';

const GAP = 20;

const Styles = labelStyles('InAppSidebar', {
  root: ({
    isCollapsed,
    isFullWidth,
    isShadowed,
  }: Pick<InAppSidebarProps, 'isCollapsed' | 'isFullWidth' | 'isShadowed'>) => [
    {
      position: 'relative',
      // Create grid to stretch content so that it scrolls.
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: Colors.neutral100,
      padding: '10px 10px 0 10px',
      // Necessary to set explicitly so `transition` works.
      //width: '100%',
      //transition: Animation.transitionValue,
      // Noticed some jankiness with `transition` and `animation`.
      // This appears to improve it at least a bit.
      willChange: 'transform, width',
    },

    !isFullWidth &&
      withModifier('constrainedWidth', {
        width: UNIVERSAL_SIDEBAR_WIDTH,
        maxWidth: '100vw',
      }),

    !isFullWidth &&
      withModifier('bordered', {
        // Add a border-right w/o the border actually taking
        // up any box model space. border-right reduces the inner
        // width of the content and causes horizontal scrolling
        // which makes this necessary.
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 1,
          backgroundColor: Colors.gray20,
        },
      }),

    // Sidebar shadow. Using a pseudo element instead of `box-shadow`
    // so the shadow only goes in one direction.
    isShadowed &&
      withModifier('shadowed', {
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '100%',
          bottom: 0,
          width: 10,
          pointerEvents: 'none',
          background: `linear-gradient(
            90deg,
            rgba(0, 0, 0, 0.10) 0%,
            rgba(0, 0, 0, 0.03) 50%,
            rgba(0, 0, 0, 0.00) 100%
          )`,
        },
      }),

    isCollapsed &&
      withModifier('collapsed', {
        width: UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
        overflow: 'visible',
      }),
  ],

  header: {
    flex: 'none',
  },

  content: ({ isCollapsed }: Pick<InAppSidebarProps, 'isCollapsed'>) => [
    {
      overflow: 'auto',
      paddingTop: 5,
      paddingBottom: 5,
      flexBasis: 'auto',
      flexShrink: 0,
      flexGrow: 0,

      // Hide scrollbar, especially on Windows and in Firefox on macOS.
      // Shouldn't be a usability issue since a) scrolling is unlikely
      // to be necessary for most screens, and b) scrolling should still
      // be obvious due to clipped content.
      scrollbarWidth: 'none',
      '::-webkit-scrollbar': {
        display: 'none',
      },
    },
    isCollapsed &&
      withModifier('collapsed', {
        overflow: 'visible',
      }),
  ],

  links: ({ isCollapsed }: Pick<InAppSidebarProps, 'isCollapsed'>) => [
    {
      alignContent: 'start',
      display: 'grid',
      gap: 5,
      listStyle: 'none',
      ...(!isCollapsed && { padding: '0 2px' }),
    },
  ],

  footer: {
    flex: 'none',
    paddingBottom: GAP,
  },
});

export type InAppSidebarProps = {
  /**
   * The `id` of the account the user is currently switched to.
   */
  currentAccountId: number;
  /**
   * Indicates whether the navigation is collapsed down to icons.
   */
  isCollapsed: boolean;
  /**
   * Whether the sidebar is as wide as it can be.
   * When `true`, will render as wide as it can.
   * When `false`, will constrain to a `max-width`.
   */
  isFullWidth?: boolean;
  /**
   * Whether the sidebar has a shadow. Should only be used when
   * the sidebar is floating.
   */
  isShadowed?: boolean;
  /**
   * Optional handler for when anywhere on the nav is clicked.
   */
  onClick?: (e: ReactMouseEvent) => void;
  /**
   * Optional handler for when the `nav` is hovered.
   */
  onMouseEnter?: (e: ReactMouseEvent) => void;
  /**
   Optional handler for when the `nav` is unhovered.
   */
  onMouseLeave?: (e: ReactMouseEvent) => void;
  /**
   * The `href` of the currently selected link within `links`.
   */
  selectedHref: string;
};

/**
 * @private
 * Sidebar to render when a user is on an in-app page. Includes:
 * - 'Create' action ('Create Zap' button or 'Create' dropdown)
 * - Product links
 * - Account usage
 * - Terms of Service updates (if any)
 * - Call to Action (if any)
 */
const InAppSidebar = ({
  currentAccountId,
  isCollapsed = false,
  isFullWidth = true,
  isShadowed,
  onClick,
  onMouseEnter,
  onMouseLeave,
  selectedHref = '',
}: InAppSidebarProps) => {
  const { data: sideNavData } = useQuerySidebar();
  const { data: sessionData } = useQuerySession();

  const taskUsageTreatmentData = useQueryTaskUsageTreatment(
    sessionData?.email,
    sessionData?.customUserId,
    sessionData?.signupDate
  );

  const {
    data: hideTaskUsageTreatment,
    isLoading,
    isError,
  } = taskUsageTreatmentData;

  // since treatment is called on the client side,
  // this is the best solution I found to handle the case where it's not yet loaded
  // without having the component flicker away
  let shouldHideTaskUsage = isLoading;
  if (!isLoading && !isError) {
    shouldHideTaskUsage = hideTaskUsageTreatment === 'on';
  } else if (isError) {
    shouldHideTaskUsage = false;
  }

  const { getFullyQualifiedURL } = useUniversalNavContext();

  const mouseProps = {
    onClick: onClick,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
  };

  const CreateAction = sideNavData.createOptions ? (
    <CreateDropdown
      isCollapsed={isCollapsed}
      items={sideNavData.createOptions}
    />
  ) : (
    <InAppSidebarSubheaderButton
      href={
        getFullyQualifiedURL(
          '/webintent/create-zap?entry-point-method=make_a_zap_call_to_action'
        ) as string
      }
      iconName="formAdd"
      isCollapsed={isCollapsed}
      text="Create Zap"
    />
  );

  return (
    <nav
      aria-label="Site Links"
      css={Styles.root({ isCollapsed, isFullWidth, isShadowed })}
      data-testid="universalSidebar"
      id={SIDEBAR_ID}
      {...mouseProps}
      data-zds
    >
      <div css={Styles.header} data-zds>
        {CreateAction}
      </div>

      <div css={Styles.content({ isCollapsed })} data-zds>
        <ul css={Styles.links({ isCollapsed })} data-zds>
          <InAppLinks
            isCollapsed={isCollapsed}
            links={sideNavData.itemGroups}
            callout={sideNavData.callout}
            selectedHref={selectedHref}
          />
        </ul>
      </div>

      {shouldHideTaskUsage ? null : (
        <div css={Styles.footer} role="region" data-zds>
          <Divider />
          <Footer
            currentAccountId={currentAccountId}
            isCollapsed={isCollapsed}
            selectedHref={selectedHref}
          />
        </div>
      )}
    </nav>
  );
};

export default InAppSidebar;
